import { useEffect, useMemo } from "react";
import { getConversations } from "../services/copilot";
import { Conversation } from "../types/copilot";
import { useSwrManagement } from "~/hooks/useSwrManagement";
import { mutate } from "swr";
import { GET_CONVERSATIONS } from "~/constants/swrKey";
import { setTimezoneIdentifier } from "~/storages/timezone";
import { useAuthenticationContext } from "~/providers/AuthenticationProvider";

const fetcher = async (token: string) => {
  if (!token) return [];
  
  const conversations = await getConversations();

  return conversations;
};

export const useGetConversations = () => {
  const { token } = useAuthenticationContext();
  const swrKey = createSWRKey(token);

  const { data, loading } = useSwrManagement({ 
    swrKey, 
    fetcher: () => fetcher(token || "")
  });

  useEffect(() => {
    data?.forEach((item) => {
      setTimezoneIdentifier(item.assetId, item.timezoneIdentifier);
    });
  }, [data]);

  return useMemo(() => {
    return {
      conversationList: (data || ([] as Conversation[])).sort((a, b) =>
        a.latestResponseOperationDate > b.latestResponseOperationDate ? -1 : 1,
      ),
      conversationListLoading: !data || loading,
    };
  }, [data, loading]);
};

const createSWRKey = (token?: string) => {
  return [GET_CONVERSATIONS, token].filter(Boolean).join("|");
};

interface ReloadConversation {
  id: string;
  latestResponseOperationDate: string;
}

export const forceReloadConversations = (token?: string) => {
  mutate(createSWRKey(token));
};

export const reloadConversations = (conversations: ReloadConversation[], token?: string) => {
  mutate(
    createSWRKey(token),
    async (data: Conversation[] | undefined) => {
      if (!data) return data;

      const tempData = data.map((x) => {
        const latestResponseOperationDate = conversations.find(
          (y) => x.id == y.id,
        )?.latestResponseOperationDate;
        return {
          ...x,
          latestResponseOperationDate: latestResponseOperationDate
            ? latestResponseOperationDate > x.latestResponseOperationDate
              ? latestResponseOperationDate
              : x.latestResponseOperationDate
            : x.latestResponseOperationDate,
        };
      });

      const sortedData = tempData.sort((a, b) =>
        a.latestResponseOperationDate > b.latestResponseOperationDate ? -1 : 1,
      );
      return sortedData;
    },
    false,
  );
};
